import React, { useState, useMemo } from 'react';
import { Icons } from '../../utils/icons';
import { authService } from '../../services/authService';
import ObraModal from './ObraModal';
import ObraDetalle from './ObraDetalle';
import useObrasConEstado from './utils/useObrasConEstado';
import { ESTADO_PUESTA_MARCHA, ORDEN_ESTADOS_OBRA, colorAutomaticoObra, estadoColorObra } from './utils/estadosObra';

const PRIORIDAD_COLOR = { rojo: 0, amarillo: 1, verde: 2 };

function fmt(fecha) {
  if (!fecha) return '';
  const [y, m, d] = fecha.split('-');
  return `${d}/${m}/${y}`;
}

export default function ObrasView({ usuario }) {
  const { obras, loading, estadoDe } = useObrasConEstado();
  const [showModal, setShowModal] = useState(false);
  const [obraEnEdicion, setObraEnEdicion] = useState(null); // null = crear, objeto = editar
  const [obraSeleccionadaId, setObraSeleccionadaId] = useState(null);

  // Las obras que más necesitan atención (rojo) primero, para que se vean
  // de un vistazo al entrar en la pestaña.
  const obrasOrdenadas = useMemo(() => {
    return [...obras].sort((a, b) => {
      const pa = PRIORIDAD_COLOR[estadoColorObra(a.clientes || [], estadoDe)];
      const pb = PRIORIDAD_COLOR[estadoColorObra(b.clientes || [], estadoDe)];
      if (pa !== pb) return pa - pb;
      return (a.titulo || '').localeCompare(b.titulo || '', 'es');
    });
  }, [obras, estadoDe]);

  const obraSeleccionada = obras.find(o => o.id === obraSeleccionadaId) || null;

  const handleNueva = () => { setObraEnEdicion(null); setShowModal(true); };
  const handleEditar = (obra) => { setObraEnEdicion(obra); setObraSeleccionadaId(null); setShowModal(true); };

  async function handleEliminar(obra) {
    if (!window.confirm(`¿Eliminar la obra "${obra.titulo}"? Esto no borra los clientes, solo la obra.`)) return;
    try {
      const resp = await fetch('/.netlify/functions/manageObras', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ action: 'eliminar', id: obra.id }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      setObraSeleccionadaId(null);
    } catch (e) { alert('Error al eliminar: ' + e.message); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-gray-500 text-sm">Seguimiento de obras Ariston — clientes pendientes de puesta en marcha</p>
        <button onClick={handleNueva}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors">
          <Icons.Plus className="w-4 h-4" />Nueva obra
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
          Cargando obras...
        </div>
      ) : obras.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg border p-10 text-center text-gray-400">
          Todavía no hay obras registradas.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {obrasOrdenadas.map(obra => {
            const clientes = obra.clientes || [];
            const conteos = ORDEN_ESTADOS_OBRA.reduce((acc, e) => ({ ...acc, [e]: clientes.filter(c => estadoDe(c) === e).length }), {});
            return (
              <button key={obra.id} onClick={() => setObraSeleccionadaId(obra.id)}
                className="text-left bg-white rounded-2xl shadow-lg border p-5 hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colorAutomaticoObra(clientes, estadoDe) }} />
                  <h3 className="font-bold text-gray-900 truncate flex-1">{obra.titulo}</h3>
                  {obra.fecha && <span className="text-xs text-gray-400 flex-shrink-0">{fmt(obra.fecha)}</span>}
                </div>
                {obra.direccion && <p className="text-xs text-gray-500 mb-3 truncate">{obra.direccion}</p>}
                <div className="flex gap-1.5 flex-wrap">
                  {ORDEN_ESTADOS_OBRA.map(e => conteos[e] > 0 && (
                    <span key={e} className={`text-xs font-bold px-2 py-0.5 rounded-full ${ESTADO_PUESTA_MARCHA[e].bg} ${ESTADO_PUESTA_MARCHA[e].text}`}>
                      {conteos[e]} {ESTADO_PUESTA_MARCHA[e].label}
                    </span>
                  ))}
                  {clientes.length === 0 && <span className="text-xs text-gray-400">Sin clientes todavía</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showModal && (
        <ObraModal
          obra={obraEnEdicion}
          usuario={usuario}
          onClose={() => setShowModal(false)}
          onSaved={() => setShowModal(false)}
        />
      )}

      {obraSeleccionada && (
        <ObraDetalle
          obra={obraSeleccionada}
          usuario={usuario}
          onClose={() => setObraSeleccionadaId(null)}
          onEditar={() => handleEditar(obraSeleccionada)}
          onEliminar={() => handleEliminar(obraSeleccionada)}
        />
      )}
    </div>
  );
}
